/**
 * 63.4 step 8 (lane E makes them, lane F wires them): a tree's damaged and destroyed forms, per species, in the stands'
 * style. Since step 6 (#630) a tree has health (`GameState.floraHealth`, 100 whole) and a felled one is `alive` false,
 * but nothing showed either. The owner's direction (decision 130 (a)): "work towards partial destruction over time" — so
 * the damaged form takes a `level` (0 whole … 1 nearly gone) and is the first of possibly several stages.
 *
 * Three forms, each a model group at the kit's scale (scale 1, no yaw, base at y = 0) exactly as `treeModel` returns the
 * whole tree, so the stands merge them into prototypes the same way (`mergeModel`) and swap one per instance:
 *   - `damaged` — limbs gone (each leaves a broken stub with pale wood at the break), the crown thinned (a share of its
 *     lumps or tiers blown away, the rest smaller), the top blown out at the higher levels, scorch on the trunk. Same
 *     place, same yaw as the tree.
 *   - `stump` — the trunk's foot, a fifth of the tree's height, with a jagged top of pale splinters. Where the tree stood.
 *   - `fallen` — the rest of the tree lying on the ground, battered, along +z from its cut end (the cut just past the
 *     stump's edge). Lane F places it at the tree with its yaw turned toward the blast's push.
 *
 * Parts are told apart by their shapes, which every kit species keeps to: the crown is blobs (icosahedra) and tiers
 * (cones), the wood is cylinders (the trunk the tallest, the rest limbs) and the bottle tree's lathe. Deterministic in
 * (species, biome colours, seed, form, level). Presentation only.
 */
import * as THREE from 'three';
import type { FloraState } from '../types';
import { BIOME_COLORS, TREE_SPECIES, type TreeSpecies, type TreeColors } from './biomeKit';
import { bakeTreeLight, buildFamilyTree } from './treeFamilies';
import { markShared } from './meshBake';
import { contactParts, contactPoint, supportOf } from './treeContact';

export type TreeForm = 'damaged' | 'stump' | 'fallen';

/** The forms' numbers, in the kit's model units (a tree is ~0.25–0.4 tall). */
export const TREE_FORM = {
  /** a tree at or under this share of its health shows the first damaged form (lane F's to wire; step 6's trees have 100) */
  damagedAt: 0.5,
  /** the default damage level of the first damaged form */
  level: 0.5,
  /** the stump's height as a share of the tree's (never under `stumpMin`) */
  stumpShare: 0.2, stumpMin: 0.025,
  /** the fallen trunk's own battering (its crown hit the ground) */
  fallenLevel: 0.35,
  scorch: '#2b2420', wood: '#cdb68a',
};

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
/** A seeded hash in [0, 1) of (seed, i, salt) — the forms' only randomness. */
function hash01(seed: number, i: number, salt: number): number {
  const h = Math.sin(seed * 12.9898 + i * 78.233 + salt * 37.719) * 43758.5453;
  return h - Math.floor(h);
}

interface Part { mesh: THREE.Mesh; box: THREE.Box3; center: THREE.Vector3; crown: boolean }
/** The tree's parts, in the tree's own space: the crown (blobs and tiers) and the wood (cylinders, the lathe). */
function partsOf(g: THREE.Group): { parts: Part[]; trunk: Part | null; height: number } {
  g.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(g.matrixWorld).invert();
  const parts: Part[] = [];
  g.traverse(o => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    const type = m.geometry.type;
    const crown = type === 'IcosahedronGeometry' || type === 'ConeGeometry' || type === 'PolyhedronGeometry';
    const box = new THREE.Box3().setFromObject(m, true).applyMatrix4(inv);
    parts.push({ mesh: m, box, center: box.getCenter(new THREE.Vector3()), crown });
  });
  const wood = parts.filter(p => !p.crown && (p.mesh.geometry.type === 'CylinderGeometry' || p.mesh.geometry.type === 'LatheGeometry'));
  // 70.2: a trunk in several lengths (treeFamilies' bent trunks) is marked: its foot is the one on the ground; else the tallest
  const marked = wood.filter(p => p.mesh.userData.trunk);
  const trunk = marked.length
    ? marked.reduce((best, p) => (p.box.min.y < best.box.min.y ? p : best))
    : wood.reduce<Part | null>((best, p) => (!best || p.box.max.y - p.box.min.y > best.box.max.y - best.box.min.y ? p : best), null);
  const height = parts.reduce((h, p) => Math.max(h, p.box.max.y), 0);
  return { parts, trunk, height };
}

/** The whole tree as the stands build it (treeStands `treeModel`): scale 1, no yaw, the light baked in. */
function wholeTree(species: TreeSpecies, colors: TreeColors, seed: number, steps = 0): THREE.Group {
  const g = buildFamilyTree(species, colors, seed, steps).group;
  bakeTreeLight(g);
  return g;
}

/** A trunk's radius at its foot (a cylinder's bottom radius; the lathe's widest). */
function footRadius(trunk: Part): number {
  const geo = trunk.mesh.geometry as THREE.CylinderGeometry;
  if (geo.type === 'CylinderGeometry') return geo.parameters.radiusBottom * Math.max(trunk.mesh.scale.x, trunk.mesh.scale.z);
  return (trunk.box.max.x - trunk.box.min.x) / 2;
}

/** A broken end: a few pale splinters standing out of a cut at `at` (tree space), up along `axis`, `r` across. */
function splinters(parent: THREE.Object3D, at: THREE.Vector3, axis: THREE.Vector3, r: number, seed: number, bark: string, maxLen = Infinity): void {
  const up = axis.clone().normalize();
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), up);
  const cap = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.92, r * 0.92, r * 0.18, 6), mat(TREE_FORM.wood));   // the pale wood of the break
  cap.position.copy(at); cap.quaternion.copy(q);
  parent.add(cap);
  const n = 3 + Math.floor(hash01(seed, 0, 5) * 2);
  for (let k = 0; k < n; k++) {
    const a = (k / n) * Math.PI * 2 + hash01(seed, k, 6) * 0.8, len = Math.min(maxLen, r * (0.9 + hash01(seed, k, 7) * 1.4));
    const s = new THREE.Mesh(new THREE.BoxGeometry(r * 0.32, len, r * 0.22), mat(k % 2 ? TREE_FORM.wood : bark));
    const off = new THREE.Vector3(Math.cos(a) * r * 0.55, len / 2, Math.sin(a) * r * 0.55).applyQuaternion(q);
    s.position.copy(at).add(off);
    s.quaternion.copy(q).multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.sin(a) * 0.35, a, -Math.cos(a) * 0.35)));
    parent.add(s);
  }
}

/**
 * Scorch on the trunk: a few dark patches on its surface, each kept inside the band `f0` … `f1` of its length — laid in
 * the trunk's own frame (70.1: round its foot's radius on the upright through its box's centre, a patch hung off a
 * leaning or tapering trunk), sized by the trunk's radius where it lies.
 */
function scorch(trunk: THREE.Mesh, f0: number, f1: number, seed: number, n: number): void {
  const geo = trunk.geometry;
  // the trunk's length along its own y, and its radius at a height on it (a cylinder's taper; the lathe's profile)
  let lo: number, hi: number, radiusAt: (y: number) => number;
  if (geo.type === 'LatheGeometry') {
    const pts = (geo as THREE.LatheGeometry).parameters.points;
    lo = pts[0].y; hi = pts[pts.length - 1].y;
    radiusAt = y => { for (let i = 1; i < pts.length; i++) if (y <= pts[i].y || i === pts.length - 1) { const a = pts[i - 1], b = pts[i]; return a.x + (b.x - a.x) * Math.min(1, Math.max(0, (y - a.y) / Math.max(1e-9, b.y - a.y))); } return pts[0].x; };
  } else {
    const q = (geo as THREE.CylinderGeometry).parameters;
    lo = -q.height / 2; hi = q.height / 2;
    radiusAt = y => q.radiusBottom + (q.radiusTop - q.radiusBottom) * (y - lo) / q.height;
  }
  const y0 = lo + f0 * (hi - lo), y1 = lo + f1 * (hi - lo);
  trunk.updateMatrix();   // (a stump just made has not had its matrix composed)
  for (let k = 0; k < n; k++) {
    const a = hash01(seed, k, 11) * Math.PI * 2, r0 = radiusAt(y0) * 0.85, tall = Math.min(r0 * (1.4 + hash01(seed, k, 13)), (y1 - y0) * 0.8);
    const y = y0 + tall / 2 + (y1 - y0 - tall) * hash01(seed, k, 12), r = radiusAt(y) * 0.85;
    const p = new THREE.Mesh(new THREE.BoxGeometry(r * 0.9, tall, r * 0.25), mat(TREE_FORM.scorch));
    p.position.set(Math.cos(a) * r, y, Math.sin(a) * r);
    p.rotation.y = -a + Math.PI / 2;
    p.updateMatrix();
    p.applyMatrix4(trunk.matrix);   // into the trunk's parent, beside it
    trunk.parent!.add(p);
  }
}

const isCrown = (m: THREE.Mesh) => { const t = m.geometry.type; return t === 'IcosahedronGeometry' || t === 'ConeGeometry' || t === 'PolyhedronGeometry'; };

/** 70.1: whatever the blow left held up by nothing — a lump on a snapped limb, a tier over one blown away — goes with it. */
function dropStranded(g: THREE.Group): void {
  const parts = contactParts(g), from = supportOf(parts);
  parts.forEach((p, i) => { if (from[i] === undefined) p.mesh.parent?.remove(p.mesh); });
}

/**
 * The crown's surviving lumps and tiers shrunk by `s` (0 … 1), each toward the point where it touches what holds it — the
 * part it hangs from, moved as that part was — so a thinned crown stays on its tree (70.1: shrunk toward their own
 * centres, a puff elm's lumps hung clear of the trunk's top).
 */
function thinCrown(g: THREE.Group, s: number): void {
  if (s >= 1) return;
  const parts = contactParts(g), from = supportOf(parts);
  const depth = (i: number): number => { let d = 0; for (let j = from[i]; j !== undefined && j >= 0; j = from[j]) d++; return d; };
  const order = parts.map((_, i) => i).filter(i => from[i] !== undefined).sort((a, b) => depth(a) - depth(b));
  // each part's map in the model's space, x → k·x + o (the wood stays: k 1, o 0)
  const maps = parts.map(() => ({ k: 1, o: new THREE.Vector3() }));
  for (const i of order) {
    if (!isCrown(parts[i].mesh)) continue;
    const j = from[i]!;
    const q = j >= 0 ? contactPoint(parts[i], parts[j]) : null;
    const at = q ?? parts[i].points.reduce((low, p) => (p.y < low.y ? p : low), parts[i].points[0]).clone();
    const moved = j >= 0 ? at.clone().multiplyScalar(maps[j].k).add(maps[j].o) : at.clone();
    maps[i] = { k: s, o: moved.sub(at.clone().multiplyScalar(s)) };
  }
  g.updateMatrixWorld(true);
  const rootInv = new THREE.Matrix4().copy(g.matrixWorld).invert();
  parts.forEach((p, i) => {
    const { k, o } = maps[i];
    if (k === 1) return;
    const m = p.mesh, parentModel = new THREE.Matrix4().multiplyMatrices(rootInv, m.parent!.matrixWorld);
    const model = new THREE.Matrix4().multiplyMatrices(rootInv, m.matrixWorld);
    const next = new THREE.Matrix4().makeTranslation(o.x, o.y, o.z).multiply(new THREE.Matrix4().makeScale(k, k, k)).multiply(model);
    next.premultiply(parentModel.invert()).decompose(m.position, m.quaternion, m.scale);
  });
}

/**
 * Damage a whole tree in place to `level` (0 … 1): crown lumps blown away (a share growing with the level; the higher
 * lumps first, so the top goes early; each stage nests in the next), limbs snapped to stubs, what they held gone with
 * them, the rest of the crown shrunk where it hangs, scorch on the trunk.
 */
function damage(g: THREE.Group, colors: TreeColors, seed: number, level: number): void {
  const k = Math.max(0, Math.min(1, level));
  const { parts, trunk, height } = partsOf(g);
  const crown = parts.filter(p => p.crown);
  // one fixed order — the higher lumps first (the top goes first), mixed by the seed — and a count that only grows with
  // the level, so each stage has lost everything the one before it had (the stages nest)
  const order = crown.map((p, i) => ({ p, key: 0.6 * (p.center.y / Math.max(1e-9, height)) + 0.4 * hash01(seed, i, 1) })).sort((a, b) => b.key - a.key);
  const take = Math.max(0, Math.min(order.length - 1, Math.ceil(order.length * 0.6 * k - 1e-9)));   // a four-lump crown loses one by 0.33, two by 0.5
  // 70.1: taken from the outside in — a lump or tier goes only once nothing it holds is left (a fir's tier under one still
  // standing, a tier a lantern's bead hangs from), so the order's next that holds nothing; a middle tier blown away left
  // the ones over it in the air (and dropping them with it took a fallen fir's whole crown)
  const contacts = contactParts(g), held = supportOf(contacts), at = new Map(contacts.map((c, i) => [c.mesh, i]));
  const holds = (p: Part, left: Set<Part>) => [...left].some(q => held[at.get(q.mesh)!] === at.get(p.mesh));
  const gone = new Set<Part>(), left = new Set(crown);
  while (gone.size < take) {
    const next = order.find(x => left.has(x.p) && !holds(x.p, left));
    if (!next) break;
    left.delete(next.p); gone.add(next.p);
  }
  for (const p of gone) p.mesh.parent?.remove(p.mesh);
  // limbs snapped: a share of them become stubs a third as long, pale at the break
  const limbs = parts.filter(p => !p.crown && p !== trunk && !p.mesh.userData.trunk && p.mesh.geometry.type === 'CylinderGeometry');
  limbs.forEach((p, i) => {
    if (hash01(seed, i, 2) > 0.25 + 0.6 * k) return;
    const m = p.mesh, geo = m.geometry as THREE.CylinderGeometry, len = geo.parameters.height;
    const dir = new THREE.Vector3(0, 1, 0).applyQuaternion(m.quaternion);
    const base = m.position.clone().addScaledVector(dir, -len / 2);
    const keep = 0.35;
    m.scale.y = keep;
    m.position.copy(base).addScaledVector(dir, (len * keep) / 2);
    splinters(m.parent!, base.clone().addScaledVector(dir, len * keep), dir, geo.parameters.radiusTop * 1.2, seed + i, colors.bark);
  });
  // 70.1: no part left in the air — what the lost lumps and limbs held goes with them — and the survivors thinned where
  // they hang
  dropStranded(g);
  thinCrown(g, 1 - 0.35 * k);
  // scorch on the trunk
  if (trunk) scorch(trunk.mesh, 0.1, 0.7, seed, 1 + Math.round(3 * k));
}

/** The kit's species and the biome's colours for a flora entry (the stands' own choice), or null for a non-kit tree. */
function kitOf(floraType: string, biome: FloraState['biome']): { species: TreeSpecies; colors: TreeColors } | null {
  if (!(TREE_SPECIES as readonly string[]).includes(floraType)) return null;
  return { species: floraType as TreeSpecies, colors: BIOME_COLORS[biome ?? 'broadleaf'] };
}

/**
 * A tree's form as the stands build prototypes: scale 1, no yaw, base at y = 0 — `treeModel`'s whole tree, damaged,
 * stumped or fallen. `level` is the damaged form's (default `TREE_FORM.level`). Null for a non-kit tree (the giant
 * classic trees keep the flora rule's fall).
 */
export function treeFormModel(floraType: string, biome: FloraState['biome'], seed: number, form: TreeForm, level: number = TREE_FORM.level, steps = 0): THREE.Group | null {
  const kit = kitOf(floraType, biome);
  if (!kit) return null;
  const { species, colors } = kit;
  const g = wholeTree(species, colors, seed, steps);
  if (form === 'damaged') { damage(g, colors, seed, level); return g; }
  const { trunk, height } = partsOf(g);
  if (!trunk) return g;
  const r = footRadius(trunk);
  const hs = Math.min(trunk.box.max.y * 0.9, Math.max(TREE_FORM.stumpMin, height * TREE_FORM.stumpShare));
  if (form === 'stump') {
    const out = new THREE.Group();
    const stump = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.92, r * 1.08, hs, 6), mat(colors.bark));
    stump.position.y = hs / 2;
    out.add(stump);
    splinters(out, new THREE.Vector3(0, hs, 0), new THREE.Vector3(0, 1, 0), r, seed, colors.bark, hs * 0.9);   // a fat trunk's splinters no taller than its stump
    scorch(stump, 0.3, 0.85, seed, 2);
    return out;
  }
  // fallen: the tree above the cut, battered, laid along +z from just past the stump
  damage(g, colors, seed + 101, TREE_FORM.fallenLevel);
  const { trunk: t2 } = partsOf(g);
  if (t2 && t2.mesh.geometry.type === 'CylinderGeometry') {
    // the trunk now starts at the cut: shorten it from below along its own axis (a leaning trunk too), its top kept
    const m = t2.mesh, geo = m.geometry as THREE.CylinderGeometry, len = geo.parameters.height * m.scale.y;
    const dir = new THREE.Vector3(0, 1, 0).applyQuaternion(m.quaternion);
    const base = m.position.clone().addScaledVector(dir, -len / 2), top = m.position.clone().addScaledVector(dir, len / 2);
    const cut = dir.y > 0.2 ? (hs - base.y) / (dir.y * len) : 0;
    if (cut > 0 && cut < 0.95) { const keep = 1 - cut; m.scale.y *= keep; m.position.copy(top).addScaledVector(dir, -(len * keep) / 2); }
  }
  else if (t2 && t2.mesh.geometry.type === 'LatheGeometry' && t2.box.max.y > hs) {
    // a lathe trunk (the bottle tree's): squeezed to span from the cut to its top
    const m = t2.mesh;
    m.geometry.computeBoundingBox();
    const bl = m.geometry.boundingBox!.min.y, tl = m.geometry.boundingBox!.max.y, top = t2.box.max.y;
    m.scale.y = (top - hs) / (tl - bl);
    m.position.y = hs - bl * m.scale.y;
  }
  for (const p of partsOf(g).parts) if (p.box.max.y < hs) p.mesh.parent?.remove(p.mesh);   // whatever was below the cut stays with the stump
  splinters(g, new THREE.Vector3(0, hs, 0), new THREE.Vector3(0, -1, 0), r, seed + 7, colors.bark, r * 1.4);   // the trunk's broken foot (short of the stump's edge it lies past)
  const lying = new THREE.Group();
  g.rotation.x = Math.PI / 2;                       // its top along +z
  g.position.set(0, r, -hs + r * 1.6);              // resting on its bark, the cut just past the stump's edge
  lying.add(g);
  return lying;
}

/** The species with forms (the kit's), for the sheet and lane F. */
export const TREE_FORM_SPECIES: readonly TreeSpecies[] = TREE_SPECIES;
